// src/ProfileCreation.js
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import { useNavigate } from 'react-router-dom';
import barangaysData from './barangays.json';
import { TbUser, TbMail, TbPhone, TbCalendar, TbGenderMale, TbHome, TbMapPin, TbPhoto, TbCheck, TbLoader } from 'react-icons/tb';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';

function ProfileCreation() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    birth_date: '',
    gender: '',
    mobileNumber: '',
    email: '',
    houseStreet: '',
    subdivision: '',
    barangay: '',
    city: 'Legazpi City',
    province: 'Albay',
    zip: '4500',
    addressLabel: '',
    avatar_url: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);

  useEffect(() => {
      document.title = "Profile";
    }, []);
  // Fetch user data and existing profile on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Pre-fill email from auth data
        setForm(prev => ({
          ...prev,
          email: user.email || ''
        }));

        // Check if profile exists
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (existingProfile) {
          // Pre-fill form with existing profile data
          setForm({
            firstName: existingProfile.first_name || '',
            lastName: existingProfile.last_name || '',
            birth_date: existingProfile.birth_date || '',
            gender: existingProfile.gender || '',
            mobileNumber: existingProfile.mobile_number || '',
            email: existingProfile.email || '',
            houseStreet: existingProfile.house_street || '',
            subdivision: existingProfile.subdivision || '',
            barangay: existingProfile.barangay || '',
            city: existingProfile.city || 'Legazpi City',
            province: existingProfile.province || 'Albay',
            zip: existingProfile.zip || '4500',
            addressLabel: existingProfile.address_label || '',
            avatar_url: existingProfile.avatar_url || ''
          });
          setAvatarPreview(existingProfile.avatar_url || '');
        }
      }
    };
    fetchUserData();
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setAvatarFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      setError("You must be logged in to create a profile.");
      setLoading(false);
      return;
    }

    try {
      let avatarUrl = form.avatar_url;

      // Upload new avatar if one was selected
      if (avatarFile) {
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = avatarFile.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, avatarFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        avatarUrl = publicUrl;
      }

      // Check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const profileData = {
        user_id: user.id,
        email: form.email,
        first_name: form.firstName,
        last_name: form.lastName,
        birth_date: form.birth_date,
        mobile_number: form.mobileNumber,
        gender: form.gender,
        house_street: form.houseStreet,
        subdivision: form.subdivision,
        barangay: form.barangay,
        city: form.city,
        province: form.province,
        zip: form.zip,
        address_label: form.addressLabel,
        avatar_url: avatarUrl,
        status: 'active'
      };

      // Only set role to 'resident' for new profiles or if role is empty
      if (!existingProfile || !existingProfile.role) {
        profileData.role = 'resident';
      } else {
        profileData.role = existingProfile.role;  // Preserve existing role
      }

      let error;
      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update(profileData)
          .eq('id', existingProfile.id);
        error = updateError;
      } else {
        // Create new profile
        const { error: insertError } = await supabase
          .from('profiles')
          .insert([profileData]);
        error = insertError;
      }

      if (error) throw error;

      // Redirect to announcements after successful profile creation/update
      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div className="header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Mobile Hamburger Menu */}
              <MobileHamburger
                isOpen={showMobileMenu}
                onToggle={() => setShowMobileMenu(prev => !prev)}
              />
              <div className="title-text">Profile Settings</div>
            </div>
          </div>
          <ProfileMenu />
        </div>
        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />
        
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Avatar Section */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfe 100%)',
            borderRadius: 16,
            padding: 32,
            marginBottom: 24,
            boxShadow: '0 4px 20px rgba(8, 177, 199, 0.1)',
            border: '1px solid rgba(8, 177, 199, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ 
              position: 'relative',
              display: 'inline-block',
              marginBottom: '20px'
            }}>
              <div style={{ 
                width: '140px', 
                height: '140px', 
                borderRadius: '50%', 
                overflow: 'hidden',
                border: '4px solid #08b1c7',
                position: 'relative',
                background: '#f8fcfd',
                boxShadow: '0 8px 24px rgba(8, 177, 199, 0.2)'
              }}>
                <img 
                  src={avatarPreview || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg'} 
                  alt="Profile" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }} 
                />
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
                id="avatar-upload"
              />
              <label 
                htmlFor="avatar-upload"
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#08b1c7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '18px',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.backgroundColor = '#0a7a89';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.backgroundColor = '#08b1c7';
                }}
                title="Upload Profile Photo"
              >
                <TbPhoto />
              </label>
            </div>
            <h3 style={{ 
              color: '#08b1c7', 
              marginBottom: '8px',
              fontSize: '1.2rem',
              fontWeight: '600'
            }}>
              Profile Photo
            </h3>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '0.9rem',
              margin: 0
            }}>
              Click the camera icon to upload a new photo
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="profile-main-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: 24,
            marginBottom: 24
          }}>
            {/* Personal Information Section */}
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfe 100%)',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 4px 20px rgba(8, 177, 199, 0.1)',
              border: '1px solid rgba(8, 177, 199, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 24,
                paddingBottom: 16,
                borderBottom: '2px solid rgba(8, 177, 199, 0.1)'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #08b1c7 0%, #0a7a89 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 16,
                  color: 'white',
                  fontSize: '24px'
                }}>
                  <TbUser />
                </div>
                <div>
                  <h3 style={{ 
                    color: '#2c3e50', 
                    margin: 0,
                    fontSize: '1.4rem',
                    fontWeight: '600'
                  }}>
                    Personal Information
                  </h3>
                  <p style={{ 
                    color: '#6b7280', 
                    margin: 0,
                    fontSize: '0.9rem'
                  }}>
                    Your basic personal details
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 20 }}>
                <div className="profile-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={{ position: 'relative' }}>
                    <div className="profile-icon" style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#08b1c7',
                      fontSize: '18px',
                      zIndex: 1
                    }}>
                      <TbUser />
                    </div>
                    <input 
                      className="auth-input" 
                      type="text" 
                      name="firstName" 
                      placeholder="First Name" 
                      value={form.firstName} 
                      onChange={handleChange} 
                      required 
                      style={{ paddingLeft: '48px' }}
                    />
                  </div>
                  <div style={{ position: 'relative' }}>
                    <div className="profile-icon" style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#08b1c7',
                      fontSize: '18px',
                      zIndex: 1
                    }}>
                      <TbUser />
                    </div>
                    <input 
                      className="auth-input" 
                      type="text" 
                      name="lastName" 
                      placeholder="Last Name" 
                      value={form.lastName} 
                      onChange={handleChange} 
                      required 
                      style={{ paddingLeft: '48px' }}
                    />
                  </div>
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbCalendar />
                  </div>
                  <input 
                    className="auth-input" 
                    type="date" 
                    name="birth_date" 
                    placeholder="Date of Birth" 
                    value={form.birth_date} 
                    onChange={handleChange} 
                    required 
                    style={{ paddingLeft: '48px' }}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbGenderMale />
                  </div>
                  <select 
                    className="auth-input" 
                    name="gender" 
                    value={form.gender} 
                    onChange={handleChange} 
                    required
                    style={{ paddingLeft: '48px' }}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbPhone />
                  </div>
                  <input 
                    className="auth-input" 
                    type="text" 
                    name="mobileNumber" 
                    placeholder="Mobile Number" 
                    value={form.mobileNumber} 
                    onChange={handleChange} 
                    required 
                    style={{ paddingLeft: '48px' }}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbMail />
                  </div>
                  <input 
                    className="auth-input" 
                    type="email" 
                    name="email" 
                    placeholder="Email Address" 
                    value={form.email} 
                    onChange={handleChange} 
                    required 
                    style={{ paddingLeft: '48px' }}
                  />
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfe 100%)',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 4px 20px rgba(8, 177, 199, 0.1)',
              border: '1px solid rgba(8, 177, 199, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 24,
                paddingBottom: 16,
                borderBottom: '2px solid rgba(8, 177, 199, 0.1)'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #08b1c7 0%, #0a7a89 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 16,
                  color: 'white',
                  fontSize: '24px'
                }}>
                  <TbMapPin />
                </div>
                <div>
                  <h3 style={{ 
                    color: '#2c3e50', 
                    margin: 0,
                    fontSize: '1.4rem',
                    fontWeight: '600'
                  }}>
                    Address Information
                  </h3>
                  <p style={{ 
                    color: '#6b7280', 
                    margin: 0,
                    fontSize: '0.9rem'
                  }}>
                    Your current residential address
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 20 }}>
                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbHome />
                  </div>
                  <input 
                    className="auth-input" 
                    type="text" 
                    name="houseStreet" 
                    placeholder="House Number / Street Address" 
                    value={form.houseStreet} 
                    onChange={handleChange} 
                    required 
                    style={{ paddingLeft: '48px' }}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbHome />
                  </div>
                  <input 
                    className="auth-input" 
                    type="text" 
                    name="subdivision" 
                    placeholder="Subdivision (Optional)" 
                    value={form.subdivision} 
                    onChange={handleChange} 
                    style={{ paddingLeft: '48px' }}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbMapPin />
                  </div>
                  <select 
                    className="auth-input" 
                    name="barangay" 
                    value={form.barangay} 
                    onChange={handleChange} 
                    required
                    style={{ paddingLeft: '48px' }}
                  >
                    <option value="">Select Barangay</option>
                    {barangaysData.map(barangay => (
                      <option key={barangay.number} value={barangay.name}>{barangay.name}</option>
                    ))}
                  </select>
                </div>

                <div className="profile-city-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <input className="auth-input" type="text" name="city" value="Legazpi City" disabled />
                  <input className="auth-input" type="text" name="province" value="Albay" disabled />
                  <input className="auth-input" type="text" name="zip" value="4500" disabled />
                </div>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#08b1c7',
                    fontSize: '18px',
                    zIndex: 1
                  }}>
                    <TbHome />
                  </div>
                  <input 
                    className="auth-input" 
                    type="text" 
                    name="addressLabel" 
                    placeholder="Address Label (e.g., Home, Work)" 
                    value={form.addressLabel} 
                    onChange={handleChange} 
                    required 
                    style={{ paddingLeft: '48px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Error and Message */}
          {error && (
            <div style={{
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              border: '1px solid #fca5a5',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              color: '#dc2626',
              fontWeight: 500,
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          
          {message && (
            <div style={{
              background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
              border: '1px solid #6ee7b7',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              color: '#065f46',
              fontWeight: 500,
              textAlign: 'center'
            }}>
              {message}
            </div>
          )}

          {/* Save Button */}
          <div style={{ 
            textAlign: 'center',
            marginTop: 32
          }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                background: loading ? '#9ca3af' : 'linear-gradient(135deg, #08b1c7 0%, #0a7a89 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '16px 48px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: loading ? 'none' : '0 4px 16px rgba(8, 177, 199, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: '0 auto',
                minWidth: '200px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 24px rgba(8, 177, 199, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(8, 177, 199, 0.3)';
                }
              }}
            >
              {loading ? (
                <>
                  <TbLoader style={{ animation: 'spin 1s linear infinite' }} />
                  Saving Profile...
                </>
              ) : (
                <>
                  <TbCheck />
                  Save Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @media (max-width: 900px) {
            .profile-main-grid {
              grid-template-columns: 1fr !important;
            }
            .profile-icon {
              display: none !important;
            }
            .auth-input {
              padding-left: 16px !important;
            }
          }
          
          @media (max-width: 600px) {
            .profile-name-grid {
              grid-template-columns: 1fr !important;
            }
            .profile-city-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

export default ProfileCreation;